import { useQuery } from "@apollo/client";
import Alert from "@mui/material/Alert";
import MuiLink from "@mui/material/Link";
import { useCurrentUser } from "hooks/useCurrentUser";
import { HELD_HARMONIZED_VIOLATION_COUNT } from "hooks/useExpiredAltIdMatchReviews";
import { Link } from "react-router-dom";

/**
 * Staff-only pointer to the expired-ID review queue.
 *
 * `severity="warning"`, never `error`: the Violations smoke spec asserts no
 * error Alert eleven times and logs in as a staff admin, so an error banner here
 * would fail the whole file.
 *
 * Gated on the ORIGINAL user, not the effective one. Emulation is how staff
 * normally open a tenant's Violations screen, and under emulation the held rows
 * are suppressed from the grid — which is exactly when someone needs to be told
 * they exist. The queue itself stays real-staff-only, so while emulating the
 * banner says so instead of offering a link that would bounce them.
 */
export function HeldViolationsBanner({ tenantId }: { tenantId: string }) {
  const { isOriginalUserStaff, isEmulating } = useCurrentUser();
  const { data } = useQuery(HELD_HARMONIZED_VIOLATION_COUNT, {
    variables: { tenantId },
    skip: !tenantId || !isOriginalUserStaff,
  });

  const count = data?.heldHarmonizedViolationCount ?? 0;
  if (!isOriginalUserStaff || count === 0) {
    return null;
  }

  return (
    <Alert severity="warning" variant="outlined">
      {count} violation{count === 1 ? " is" : "s are"} matched to a facility only through an expired
      EPA or FRS ID, and stay hidden from this customer until Encamp reviews the match.{" "}
      {isEmulating ? (
        "Exit emulation to open the review queue."
      ) : (
        <MuiLink component={Link} to="/staff/violation-match-review">
          Open the review queue
        </MuiLink>
      )}
    </Alert>
  );
}
